/**
 * Rôles utilisateurs back-office — miroir front de server/utils/roles.js
 *
 * ⚠️ RÈGLE : toute comparaison de rôle (accès, affichage, filtre) DOIT passer
 * par les prédicats exportés ici (hasAdminRights, isSuperAdmin, isCommercial),
 * jamais par une comparaison littérale `role === 'admin'`.
 *
 * Un super_admin a EXACTEMENT tous les droits d'un admin, sans exception, et
 * est le seul à accéder en plus au panneau d'activité commerciale. Comparer
 * littéralement à 'admin' fait silencieusement perdre ses droits à un
 * super_admin : c'est le bug que ce module empêche.
 */

export const ROLES = {
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
  COMMERCIAL: 'commercial',
}

// Rôles disposant des droits d'administration (gestion des utilisateurs,
// journaux, modération, emails admin, etc.).
export const ADMIN_ROLES = [ROLES.ADMIN, ROLES.SUPER_ADMIN]

/**
 * True si le rôle dispose des droits d'administration (admin OU super_admin).
 * C'est le prédicat à utiliser par défaut partout où le code voulait dire
 * « c'est un administrateur ».
 */
export function hasAdminRights(role) {
  return ADMIN_ROLES.includes(role)
}

/**
 * True si le rôle est spécifiquement super_admin — seul rôle avec accès au
 * panneau d'activité commerciale.
 */
export function isSuperAdmin(role) {
  return role === ROLES.SUPER_ADMIN
}

/**
 * True si le rôle est commercial.
 */
export function isCommercial(role) {
  return role === ROLES.COMMERCIAL
}

export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Administrateur',
  [ROLES.SUPER_ADMIN]: 'Super administrateur',
  [ROLES.COMMERCIAL]: 'Commercial',
}

/**
 * Variante de Badge (src/components/ui/Badge.vue) à utiliser pour représenter
 * un rôle. On réutilise des variantes EXISTANTES de Badge.vue plutôt que d'en
 * inventer une nouvelle : super_admin reprend 'danger' pour rester visuellement
 * distinct d'admin ('admin', bleu) et de commercial ('commercial', turquoise).
 */
export const ROLE_BADGE_VARIANTS = {
  [ROLES.ADMIN]: 'admin',
  [ROLES.SUPER_ADMIN]: 'danger',
  [ROLES.COMMERCIAL]: 'commercial',
}

export function roleBadgeVariant(role) {
  return ROLE_BADGE_VARIANTS[role] || 'secondary'
}

export function roleLabel(role) {
  return ROLE_LABELS[role] || role
}

/**
 * Libellés COURTS, pour les surfaces denses où le libellé complet casserait la
 * mise en page : cellule d'identité d'un tableau, pastille de liste, chip.
 * Ailleurs (fiche utilisateur, formulaire, menu profil), utiliser `roleLabel`.
 */
export const ROLE_SHORT_LABELS = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.SUPER_ADMIN]: 'Super admin',
  [ROLES.COMMERCIAL]: 'Commercial',
}

export function roleShortLabel(role) {
  return ROLE_SHORT_LABELS[role] || roleLabel(role)
}

/**
 * Rôles complémentaires — miroir de server/utils/roles.js. Posés en plus du
 * rôle principal, jamais déduits de lui : un admin n'a pas `pointeuse` d'office.
 */
export const ROLES_COMPLEMENTAIRES = {
  POINTEUSE: 'pointeuse',
}

export const ROLE_COMPLEMENTAIRE_LABELS = {
  [ROLES_COMPLEMENTAIRES.POINTEUSE]: 'Pointeuse',
}

export function aLeRoleComplementaire(user, role) {
  const roles = user?.rolesComplementaires
  return Array.isArray(roles) && roles.includes(role)
}

export function roleComplementaireLabel(role) {
  return ROLE_COMPLEMENTAIRE_LABELS[role] || role
}
